import React, { useEffect } from 'react'
import ContactButton from 'Root/Shared/components/header/ContactButton'
import PuxAILanguageSelector from 'Root/Shared/components/PuxLanguageSelector/PuxAILanguageSelector'
import { IHeaderData } from 'Root/Shared/queries/layout-queries'
import { useEditorStatus } from 'Root/Shared/utils/edit/useEditorStatus'
import { usePreviewStatus } from 'Root/Shared/utils/edit/usePreviewStatus'
import GoogleSearch from 'Shared/components/GoogleSearch/GoogleSearch'
import HeaderMobileNav, {
  HeaderData,
} from 'Shared/components/header/HeaderMobileNav'
import HeaderNav from 'Shared/components/header/HeaderNav'
import PuxButton from 'Shared/components/PuxButton/PuxButton'
import PuxIcon from 'Shared/components/PuxIcon/PuxIcon'
import { localizedPathType } from 'Shared/components/PuxLanguageSelector/PuxLanguageSelector'
import { LinkType } from 'Shared/types/Link'
import { getFromConfig } from 'Shared/utils/localeURL'
import PuxLink from 'Shared/utils/PuxLink'
import useWindowDimensions from 'Shared/utils/useWindowDimensions'
import { t } from 'ttag'

//@ts-ignore
import { ReactComponent as Logo } from '../../svg/Easy8_logo.svg'
//@ts-expect-error SVG component imports are provided by Gatsby's SVGR loader.
import { ReactComponent as LogoRedmineEdition } from '../../svg/Easy8_logo_redmine_edition.svg'
//@ts-ignore
import { ReactComponent as LogoMobile } from '../../svg/Easy8_logo_sticky.svg'

const localizedHomepageUrl = `/${getFromConfig(
  process.env.LOCALE ?? `en`,
  `urlPrefix`,
  process.env.PROJECT ?? `EasyRedmineGatsby`
)}`

interface HeaderPropsType {
  customHeaderClass?: string
  customHeaderFnc: (duplicatedNav: boolean) => void
  style?: string
  cta?: LinkType
  landingPageNav?: HeaderData
  landingPageLogo?: string | null
  localizedPaths?: localizedPathType[]
  headerData: IHeaderData
}

const Header: React.FunctionComponent<HeaderPropsType> = (props) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [isScrolled, setScrolled] = React.useState(false)
  const [respoWrapperTop, setRespoWrapperTop] = React.useState(56)
  const headerRef = React.useRef<HTMLElement>(null)
  const headerData: HeaderData | any = props.headerData.menu[0].menuItemsList
  const { width } = useWindowDimensions()
  const localizedPaths = props.localizedPaths
  const isDesktop = width > 1199 || width === 0
  const isRespo = width <= 1199 && width !== 0
  const isLogoSmall = (width >= 1200 && width < 1280) || width === 838
  const isRedmineEdition =
    props.style === `landing` && props.landingPageLogo === `redmine-edition`
  // Crop the SVG's empty export margins without changing the logo artwork.
  const headerLogo = isRedmineEdition ? (
    <LogoRedmineEdition viewBox='58 30 1018 168.85' />
  ) : (
    <Logo />
  )
  const logoClass =
    isRedmineEdition && !isLogoSmall
      ? `Header-logo Header-logo--redmine-edition`
      : `Header-logo`
  const isEditor = useEditorStatus()
  const isPreview = usePreviewStatus()

  const classList = [`Header`]

  if (isOpen) classList.push(`is-opened`)
  if (isScrolled) classList.push(`is-scrolled`)
  if (props.style === `landing`) classList.push(`Header--landing`)
  if (props.customHeaderClass) classList.push(props.customHeaderClass)

  const updateDimensions = () => {
    if (window.scrollY > 0) {
      return setScrolled(true)
    }

    return setScrolled(false)
  }

  useEffect(() => {
    document
      .querySelectorAll(`.Header-menuRespo a`)
      .forEach((link) => link.addEventListener(`click`, () => setIsOpen(false)))
  })

  useEffect(() => {
    window.addEventListener(`scroll`, updateDimensions)

    return () => {
      window.removeEventListener(`scroll`, updateDimensions)
    }
  }, [isScrolled])

  useEffect(() => {
    if (!isOpen) return

    let frameId: number | null = null

    const updateRespoWrapperTop = () => {
      const headerBottom = headerRef.current?.getBoundingClientRect().bottom

      if (headerBottom !== undefined) setRespoWrapperTop(headerBottom)
    }

    const scheduleRespoWrapperTopUpdate = () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId)

      frameId = window.requestAnimationFrame(() => {
        updateRespoWrapperTop()
        frameId = null
      })
    }

    const resizeObserver =
      typeof ResizeObserver !== `undefined`
        ? new ResizeObserver(scheduleRespoWrapperTopUpdate)
        : null
    const mutationObserver = new MutationObserver(scheduleRespoWrapperTopUpdate)
    const observedLayout = headerRef.current?.parentElement ?? document.body

    scheduleRespoWrapperTopUpdate()
    resizeObserver?.observe(observedLayout)
    mutationObserver.observe(observedLayout, { childList: true })
    window.addEventListener(`resize`, scheduleRespoWrapperTopUpdate)
    window.addEventListener(`scroll`, scheduleRespoWrapperTopUpdate)

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId)

      resizeObserver?.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener(`resize`, scheduleRespoWrapperTopUpdate)
      window.removeEventListener(`scroll`, scheduleRespoWrapperTopUpdate)
    }
  }, [isOpen])

  return (
    <header ref={headerRef} className={classList.join(` `)}>
      <div className='Header-container'>
        <PuxLink className={logoClass} to={localizedHomepageUrl}>
          {isLogoSmall ? <LogoMobile /> : headerLogo}
        </PuxLink>
        {isDesktop && (
          <HeaderNav
            headerData={
              props.landingPageNav ? props.landingPageNav : headerData
            }
          />
        )}
        {isDesktop && (
          <>
            <a
              href='https://client-zone.easysoftware.com/en/login'
              className={`Header-user`}
              aria-label='Client zone'
            >
              <PuxIcon name='users/account' size={24} decorative />
            </a>
            {!(isEditor || isPreview) && (
              <div className={`Header-search`}>
                <GoogleSearch searchType='header' />
              </div>
            )}
            <div className={`Header-language`}>
              <PuxAILanguageSelector localizedPaths={localizedPaths} />
            </div>
          </>
        )}
        <div className='Header-trial'>
          <ContactButton />
          <PuxButton
            buttonType='primary'
            buttonSize={isRespo ? `small` : `medium`}
            buttonLink={{
              url: [props.cta ? props.cta.url : `#trial`],
              internal: [false],
              text: [props.cta ? props.cta.text : t`Redmine.Header.FreeTrial`],
            }}
            onAnchorLinkClick={() => {
              document
                .querySelector<HTMLInputElement>(
                  `#mauticform_trialrequest #company-email`
                )
                ?.focus()
            }}
          />
        </div>
        {isRespo && (
          <button
            className='Header-burger'
            type='button'
            aria-label={isOpen ? `Close menu` : `Open menu`}
            aria-expanded={isOpen}
            onClick={() => {
              if (!isOpen) {
                const headerBottom = headerRef.current?.getBoundingClientRect()
                  .bottom

                if (headerBottom !== undefined) setRespoWrapperTop(headerBottom)
              }

              setIsOpen(!isOpen)
            }}
          >
            <PuxIcon name='user-interface/menu-3' size={12} decorative />
          </button>
        )}
      </div>
      {isRespo && (
        <div
          className='Header-respoWrapper'
          style={
            {
              '--header-respo-wrapper-top': `${respoWrapperTop}px`,
            } as React.CSSProperties
          }
        >
          <GoogleSearch searchType='headerRespo' />
          <HeaderMobileNav
            headerData={
              props.landingPageNav ? props.landingPageNav : headerData
            }
          />
          <div className='Header-respoWrapperFooter'>
            <ContactButton forceButton />
            <div className='Header-respoWrapperActions'>
              <a
                href='https://client-zone.easysoftware.com/en/login'
                className={`Header-user`}
                aria-label='Client zone'
              >
                <PuxIcon name='users/account' size={24} decorative />
              </a>
              <div className={`Header-language`}>
                <PuxAILanguageSelector
                  localizedPaths={localizedPaths}
                  selectorType='nativeDropdown'
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

export default Header
